// Fuente única de los planes de suscripción (datos + helpers). Antes vivía
// inline en `Checkout.tsx`; se extrajo para compartirla con el alta enterprise
// (`EnterpriseCheckout.tsx`) sin duplicar precios/features. Ver ADR.
//
// El techo de videos NO se escribe a mano: sale de `videosPorCreditos` sobre el
// `max_fotos_mes` del propio plan, con el piso real de 5 créditos por video. Antes
// eran literales calculados sobre ~12 créditos por video y estaban desfasados del
// catálogo (ADR-2026-08-03-videos-por-plan-se-calculan-con-el-piso-real).
import { videosPorCreditos } from "../components/pricing/pricingContent";

export interface SubscriptionPlan {
  id: string;
  name: string;
  price: number; // precio en ARS (Argentina); otros países se resuelven aparte
  description: string;
  features: string[];
  max_fotos_mes: number;
}

export const plans: SubscriptionPlan[] = [
  {
    id: "emprendimiento",
    name: "Básico",
    price: 21999,
    description:
      "Pensado para pequeños emprendimientos que están buscando profesionalizar su negocio",
    features: [
      "Hasta 60 imágenes por mes",
      "Función de Edición de imágenes con IA",
      "Acceso desde computadora, tablet y celular",
      "~Generación de videos con IA",
    ],
    max_fotos_mes: 60,
  },
  {
    id: "negocio",
    name: "Pro",
    price: 69999,
    description:
      "Ideal para negocios en crecimiento que quieren imágenes de alta calidad para impulsar sus ventas",
    features: [
      "Hasta 250 imágenes por mes",
      `Hasta ${videosPorCreditos(250).toLocaleString("es-AR")} videos por mes`,
      "Función de Edición de imágenes con IA",
      "Acceso desde computadora, tablet y celular",
      "Soporte prioritario",
    ],
    max_fotos_mes: 250,
  },
  {
    id: "enterprise",
    name: "Ultra",
    price: 209999,
    description:
      "Creado para negocios con producción de imágenes y gestión catálogos grandes",
    features: [
      "Hasta 900 imágenes por mes",
      `Hasta ${videosPorCreditos(900).toLocaleString("es-AR")} videos por mes`,
      "Estudio de Video para editar y crear videos",
      "Función de Edición de imágenes con IA",
      "Acceso desde computadora, tablet y celular",
      "Soporte dedicado 24/7",
      "Mejor relación precio / imagen",
      "Asesoría estratégica para campañas",
    ],
    max_fotos_mes: 900,
  },
  {
    id: "enterprise1",
    name: "Ultra Nivel 1",
    price: 109999,
    description:
      "Creado para negocios con producción de imágenes y gestión catálogos grandes",
    features: [
      "Hasta 500 imágenes por mes",
      `Hasta ${videosPorCreditos(500).toLocaleString("es-AR")} videos por mes`,
      "Estudio de Video para editar y crear videos",
      "Función de Edición de imágenes con IA",
      "Acceso desde computadora, tablet y celular",
      "Soporte dedicado 24/7",
      "Mejor relación precio / imagen",
      "Asesoría estratégica para campañas",
    ],
    max_fotos_mes: 500,
  },
  {
    id: "enterprise2",
    name: "Ultra Nivel 2",
    price: 209999,
    description:
      "Creado para negocios con producción de imágenes y gestión catálogos grandes",
    features: [
      "Hasta 1.000 imágenes por mes",
      `Hasta ${videosPorCreditos(1000).toLocaleString("es-AR")} videos por mes`,
      "Estudio de Video para editar y crear videos",
      "Función de Edición de imágenes con IA",
      "Acceso desde computadora, tablet y celular",
      "Soporte dedicado 24/7",
      "Mejor relación precio / imagen",
      "Asesoría estratégica para campañas",
    ],
    max_fotos_mes: 1000,
  },
  {
    id: "enterprise3",
    name: "Ultra Nivel 3",
    price: 399999,
    description:
      "Creado para negocios con producción de imágenes y gestión catálogos grandes",
    features: [
      "Hasta 2.000 imágenes por mes",
      `Hasta ${videosPorCreditos(2000).toLocaleString("es-AR")} videos por mes`,
      "Estudio de Video para editar y crear videos",
      "Función de Edición de imágenes con IA",
      "Acceso desde computadora, tablet y celular",
      "Soporte dedicado 24/7",
      "Mejor relación precio / imagen",
      "Asesoría estratégica para campañas",
    ],
    max_fotos_mes: 2000,
  },
];

export function getPlanById(id: string): SubscriptionPlan | undefined {
  return plans.find((p) => p.id === id);
}

/**
 * Imágenes que SUMA un cambio de plan (upgrade o downgrade) sobre las restantes
 * actuales. Espejo del `upgradeIncrement` del payment server
 * (`atlas-payment-server/src/domain/plans.ts`, PLAN_CATALOG). En la renovación NO
 * se suma: refill a `max_fotos_mes`.
 *
 * **Se DERIVA de la cuota del plan, ya no es una tabla a mano.** El server sacó los
 * bonus de créditos: un plan da su cuota y nada más, así que el incremento de un
 * cambio de plan ES `max_fotos_mes` por definición. La tabla escrita a mano tenía
 * `negocio: 300` (250 + 50 de bonus) y `enterprise: 1100` (900 + 200), que es lo que
 * el checkout le prometía al cliente —"se sumarán 300 imágenes"— cuando el server ya
 * iba a sumar 250. Derivarlo del catálogo hace que esa diferencia no pueda volver a
 * abrirse sola. Ver ADR-2026-08-19-incremento-de-upgrade-sale-de-la-cuota.
 *
 * Si el server vuelve a separar incremento de cuota, este es el lugar donde hay que
 * volver a escribirlos a mano — y entonces sí, uno por uno.
 */
export const PLAN_UPGRADE_INCREMENTS: Record<string, number> = Object.fromEntries(
  plans.map((p) => [p.id, p.max_fotos_mes]),
);

/** ¿El id es alguno de los planes Ultra (incluye el id legacy sin nivel)? */
export function esPlanUltra(planId: string): boolean {
  return (
    planId === "enterprise" ||
    planId === "enterprise1" ||
    planId === "enterprise2" ||
    planId === "enterprise3"
  );
}

/**
 * ¿Este cambio de plan admite el cupón de descuento (hoy el "Hot Sale" del 30%
 * del primer mes)? Es la ÚNICA fuente de la regla: la consumen el catálogo
 * (`buildPlanViews`, lo que el cliente ve) y el checkout (`Checkout.tsx`, lo que
 * realmente se cobra), que antes tenían la condición duplicada a mano.
 *
 * Reglas, en orden:
 *
 * 1. **El Básico nunca lleva promo** — decisión comercial de siempre: su precio
 *    ya es el de entrada.
 * 2. **Altas de quien NUNCA pagó**: el descuento sigue vigente para Pro y Ultra.
 *    Es la promo de captación (links de la landing, banner de upsell del home) y
 *    no se tocó.
 * 3. **Clientes que ya pagan**: el descuento quedó SOLO en Básico → Pro
 *    (ADR-2026-08-18-descuento-de-upgrade-solo-basico-a-pro). Antes lo llevaba
 *    cualquier upgrade — Básico → Ultra, Pro → Ultra y hasta un cambio de nivel
 *    dentro de Ultra —, o sea que el 30% se regalaba justo en los cambios de
 *    mayor ticket.
 * 4. **Bajadas de plan**: sin descuento, como siempre (no entran en ninguna de
 *    las ramas de arriba).
 *
 * `yaFueClientePago` (`company_profiles.paid_plan_start_date is not null`) existe
 * porque dar de baja deja el plan en `gratis`, y sin ese dato una cuenta que ya
 * pagó es **indistinguible** de una nueva: cancelar, esperar el fin del ciclo y
 * volver a darse de alta devolvía el 30% en cualquier plan — o sea, un camino más
 * barato que el upgrade que la regla (3) acababa de encarecer. Se cierra del todo:
 * quien pagó alguna vez no vuelve a ver la promo de captación
 * (ADR-2026-08-18-descuento-no-revive-al-volver-a-gratis).
 *
 * `currentPlan` vacío se trata como `gratis`. Cualquier otro plan de origen que no
 * sea `emprendimiento` (planes a medida, `experto`, ids futuros) cae en "sin
 * descuento" a propósito — ante la duda con algo que COBRA, precio de lista. Por
 * eso `yaFueClientePago` es un parámetro OBLIGATORIO y no uno con default: el
 * default seguro sería `true` (sin descuento), y un default silencioso en la
 * dirección cómoda es exactamente cómo se regala plata sin que nadie lo note.
 */
export function cambioDePlanAdmiteDescuento(
  currentPlan: string | null | undefined,
  targetPlanId: string,
  yaFueClientePago: boolean,
): boolean {
  // (1) Destinos elegibles: Pro y Ultra. El Básico queda afuera siempre.
  if (targetPlanId !== "negocio" && !esPlanUltra(targetPlanId)) return false;

  // (2) Alta desde gratis: promo de captación, solo para quien nunca pagó.
  const esAltaDesdeGratis = !currentPlan || currentPlan === "gratis";
  if (esAltaDesdeGratis) return !yaFueClientePago;

  // (3) Ya paga: únicamente Básico → Pro.
  return currentPlan === "emprendimiento" && targetPlanId === "negocio";
}

/**
 * ¿Al plan ACTUAL le queda algún upgrade con descuento? Es la pregunta de las
 * superficies que prometen el cupón sin un destino fijo — hoy la barra de
 * créditos ("Mejorá tu plan con 30% OFF"): para el gratis que nunca pagó y para
 * el Básico existe un destino con promo; para Pro y Ultra no existe ninguno, así
 * que prometer el % ahí es prometer un precio que el checkout no cobra
 * (ADR-2026-08-31-barra-creditos-descuento-segun-plan). Derivado de
 * `cambioDePlanAdmiteDescuento` sobre el catálogo para que la regla comercial
 * siga teniendo una sola fuente.
 */
export function planAdmiteDescuentoDeUpgrade(
  currentPlan: string | null | undefined,
  yaFueClientePago: boolean,
): boolean {
  return plans.some((p) =>
    cambioDePlanAdmiteDescuento(currentPlan, p.id, yaFueClientePago),
  );
}

/**
 * Slug del link de alta enterprise → `planId`. SOLO planes que tienen `pln_` en
 * Rebill (`REBILL_PLAN_IDS`): el id base `enterprise` NO está, por eso se usan
 * los niveles. Cada plan = su propio link (ej. `/alta-empresa/ultra-1`).
 */
export const ENTERPRISE_PLAN_SLUGS: Record<string, string> = {
  basico: "emprendimiento",
  pro: "negocio",
  "ultra-1": "enterprise1",
  "ultra-2": "enterprise2",
  "ultra-3": "enterprise3",
};

/** Precio formateado en ARS (ej. `$109.999 ARS`). */
export function formatArs(amount: number): string {
  return `$${new Intl.NumberFormat("es-AR").format(amount)} ARS`;
}

// ── Precios y moneda por país ────────────────────────────────────────────
//
// Vivían como funciones locales dentro de `Checkout.tsx`. Se movieron acá para
// que el alta por link (`EnterpriseCheckout.tsx`) use EXACTAMENTE los mismos
// precios en vez de asumir ARS — que era el bug: un usuario de Chile veía
// precios argentinos y el formulario de Rebill. Ver ADR-592.
//
// El cuerpo de estas funciones se movió VERBATIM: mismos valores, mismo orden,
// mismas ramas. Lo único que cambió es que los flags de país (`isArgentina`,
// `isChile`, …) ahora se derivan del parámetro `pais` en vez de venir del
// scope del componente.

export interface PaisFlags {
  isArgentina: boolean;
  isChile: boolean;
  isColombia: boolean;
  isMexico: boolean;
  isPeru: boolean;
}

/** Normaliza el país y devuelve los flags. Misma lógica que tenía Checkout. */
export function paisFlags(pais: string | null | undefined): PaisFlags {
  const normalizedPais = (pais || "").toLowerCase();
  return {
    isArgentina: normalizedPais === "argentina",
    isChile: normalizedPais === "chile",
    isColombia: normalizedPais === "colombia",
    isMexico: normalizedPais === "méxico" || normalizedPais === "mexico",
    isPeru: normalizedPais === "perú" || normalizedPais === "peru",
  };
}

// Feature flag de la facturación AFIP de la SUSCRIPCIÓN. En `true` la facturación es VISIBLE para
// TODOS los usuarios de Argentina (antes quedaba OCULTA mientras AFIP estaba en homologación; solo
// los dev_testers la veían). Se prende con AFIP en PRODUCCIÓN (cert + vars en Render + AFIP_ENV=prod
// + una factura real ya emitida OK en el payment-server). Ver ADR-2026-08-13-facturacion-gate-dev-tester.
export const FACTURACION_ENABLED = true;

/** ¿Se le muestra la facturación de la suscripción a este usuario? Solo AR, y solo si el flag está
 *  prendido o es dev_tester. Único punto de verdad para gatear los accesos (Config, popup, portal). */
export function facturacionVisible(
  isArgentina: boolean,
  devTester: boolean | null | undefined,
): boolean {
  return isArgentina && (FACTURACION_ENABLED || devTester === true);
}

/** Moneda del país. Todo lo no contemplado cae a USD. */
export function currencyForPais(pais: string | null | undefined): string {
  const { isArgentina, isChile, isColombia, isMexico, isPeru } = paisFlags(pais);
  return isArgentina
    ? "ARS"
    : isChile
      ? "CLP"
      : isColombia
        ? "COP"
        : isMexico
          ? "MXN"
          : isPeru
            ? "PEN"
            : "USD";
}

/** Precio del plan en la moneda del país. `arsPrice` es el precio base (ARS). */
export function getPlanPriceForPais(
  id: string,
  arsPrice: number,
  pais: string | null | undefined,
): number {
  const { isArgentina, isChile, isColombia, isMexico, isPeru } = paisFlags(pais);
  if (isArgentina) return arsPrice;
  if (isChile) {
    switch (id) {
      case "emprendimiento":
        return 14999;
      case "negocio":
        return 49999;
      case "enterprise":
      case "enterprise1":
        return 79999;
      case "enterprise2":
        return 149999;
      case "enterprise3":
        return 299999;
      default:
        return 0;
    }
  }
  if (isColombia) {
    switch (id) {
      case "emprendimiento":
        return 59999;
      case "negocio":
        return 199000;
      case "enterprise":
      case "enterprise1":
        return 299999;
      case "enterprise2":
        return 549999;
      case "enterprise3":
        return 1099999;
      default:
        return 0;
    }
  }
  if (isMexico) {
    switch (id) {
      case "emprendimiento":
        return 299;
      case "negocio":
        return 999;
      case "enterprise":
      case "enterprise1":
        return 1699;
      case "enterprise2":
        return 2999;
      case "enterprise3":
        return 5999;
      default:
        return 0;
    }
  }
  if (isPeru) {
    switch (id) {
      case "emprendimiento":
        return 49;
      case "negocio":
        return 179;
      case "enterprise":
      case "enterprise1":
        return 299;
      case "enterprise2":
        return 549;
      case "enterprise3":
        return 1099;
      default:
        return 0;
    }
  }
  switch (id) {
    case "emprendimiento":
      return 15;
    case "negocio":
      return 50;
    case "enterprise":
    case "enterprise1":
      return 80;
    case "enterprise2":
      return 150;
    case "enterprise3":
      return 300;
    default:
      return 0;
  }
}

/** Precio formateado con la moneda y el locale del país. */
export function formatPriceForPais(
  amount: number,
  pais: string | null | undefined,
): string {
  const { isArgentina, isChile, isColombia, isMexico, isPeru } = paisFlags(pais);
  const currencyCode = currencyForPais(pais);
  const locale = isArgentina
    ? "es-AR"
    : isChile
      ? "es-CL"
      : isColombia
        ? "es-CO"
        : isMexico
          ? "es-MX"
          : isPeru
            ? "es-PE"
            : "en-US";
  // USD: precios siempre se muestran como ".99" (valor interno entero - 0.01).
  // Resto de monedas: entero sin decimales (mantienen el comportamiento previo).
  const isUSD = currencyCode === "USD";
  const displayAmount = isUSD && amount > 0 ? amount - 0.01 : amount;
  const fmt = new Intl.NumberFormat(locale, {
    minimumFractionDigits: isUSD ? 2 : 0,
    maximumFractionDigits: isUSD ? 2 : 0,
  });
  return `$${fmt.format(displayAmount)} ${currencyCode}`;
}

/**
 * Código ISO → nombre de país, en el MISMO formato que usa `CountrySelect`
 * (`components/OnboardingSelects.tsx`) y que espera `paisFlags`.
 *
 * La landing manda el país detectado como código en la URL del alta
 * (`/alta-empresa/basico?pais=CL`). Acá se traduce para poder resolver el
 * precio en moneda local desde el primer render y pre-seleccionar el país en
 * el formulario. Ver ADR-592.
 *
 * "OTHER" y cualquier código desconocido devuelven `null`: no se inventa un
 * país. Sin país, el precio cae a USD, que es el fallback correcto para todo
 * lo que no tiene tabla propia.
 */
export const PAIS_POR_CODIGO_ISO: Record<string, string> = {
  AR: 'Argentina',
  CL: 'Chile',
  CO: 'Colombia',
  MX: 'México',
  PE: 'Perú',
};

export function paisDesdeCodigoISO(
  codigo: string | null | undefined,
): string | null {
  if (!codigo) return null;
  return PAIS_POR_CODIGO_ISO[codigo.trim().toUpperCase()] ?? null;
}

/**
 * País del checkout: el código que manda la landing en `?pais=`, y si ese no
 * sirve, el país que el cliente YA eligió a mano (guardado en `profiles.pais` /
 * `company_profiles.pais`).
 *
 * El param no alcanza solo: sale de geolocalizar la IP, y una IP puede mentir.
 * Una clienta argentina entrando desde el navegador in-app de Instagram recibió
 * `?pais=OTHER` al recargar (el proxy la geolocalizó afuera). Como el país
 * quedaba vacío, `esArgentina` daba `false` y el checkout le ofrecía Stripe en
 * USD en vez del form de Rebill en ARS. El dato declarado le gana a la IP.
 * Ver ADR-2026-08-18-alta-empresa-pais-solo-de-la-url.
 */
export function resolverPaisCheckout(
  codigoDeLaUrl: string | null | undefined,
  paisGuardado: string | null | undefined,
): string {
  return paisDesdeCodigoISO(codigoDeLaUrl) ?? (paisGuardado?.trim() || '');
}
